import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, HStack, Text, VStack, Badge, IconButton } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI, MentionNotificationItem } from '../services/api';

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const BellIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

interface MentionsBellProps {
  variant?: 'icon' | 'button';
}

export const MentionsBell: React.FC<MentionsBellProps> = ({ variant = 'button' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<MentionNotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await notificationsAPI.list({ limit: 20 });
      setItems(res.data.items || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 30000);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      await load();
      setLoading(false);
    }
  };

  const openMention = async (item: MentionNotificationItem) => {
    if (!item.readAt) {
      try {
        const res = await notificationsAPI.markRead(item._id);
        setUnreadCount(res.data.unreadCount);
        setItems((prev) =>
          prev.map((row) =>
            row._id === item._id ? { ...row, readAt: res.data.item.readAt } : row,
          ),
        );
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    navigate(
      `/project/${item.project}?task=${encodeURIComponent(item.task)}&comment=${encodeURIComponent(item.commentId)}`,
    );
  };

  const markAll = async () => {
    try {
      await notificationsAPI.markAllRead();
      setUnreadCount(0);
      setItems((prev) => prev.map((row) => ({ ...row, readAt: row.readAt || new Date().toISOString() })));
    } catch {
      /* ignore */
    }
  };

  return (
    <Box ref={rootRef} position="relative" width={variant === 'button' ? '100%' : 'auto'}>
      {variant === 'button' ? (
        <Button
          variant="outline"
          borderRadius="lg"
          justifyContent="flex-start"
          width="100%"
          onClick={handleOpen}
          _hover={{ bg: 'gray.100' }}
        >
          <HStack gap={2} width="100%" justifyContent="space-between">
            <HStack gap={2}>
              <BellIcon />
              <Text>Mentions</Text>
            </HStack>
            {unreadCount > 0 && (
              <Badge colorScheme="red" borderRadius="full" px={2}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </HStack>
        </Button>
      ) : (
        <IconButton
          aria-label="Mentions"
          variant="ghost"
          borderRadius="full"
          onClick={handleOpen}
          position="relative"
        >
          <BellIcon />
          {unreadCount > 0 && (
            <Box
              position="absolute"
              top="2px"
              right="2px"
              minW="16px"
              h="16px"
              px="3px"
              borderRadius="full"
              bg="red.500"
              color="white"
              fontSize="10px"
              lineHeight="16px"
              textAlign="center"
              fontWeight="bold"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Box>
          )}
        </IconButton>
      )}

      {open && (
        <Box
          position="absolute"
          top={variant === 'button' ? 'calc(100% + 8px)' : 'calc(100% + 6px)'}
          left={variant === 'button' ? 0 : 'auto'}
          right={variant === 'button' ? 0 : 0}
          w={variant === 'button' ? '100%' : '320px'}
          maxW="92vw"
          bg="white"
          borderWidth={1}
          borderColor="gray.200"
          borderRadius="xl"
          boxShadow="xl"
          zIndex={40}
          overflow="hidden"
        >
          <HStack justifyContent="space-between" px={3} py={2} borderBottomWidth={1} borderColor="gray.100">
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              Mentions
            </Text>
            {unreadCount > 0 && (
              <Button size="xs" variant="ghost" onClick={markAll}>
                Mark all read
              </Button>
            )}
          </HStack>
          <Box maxH="360px" overflowY="auto">
            {loading && items.length === 0 ? (
              <Text fontSize="sm" color="gray.500" p={4}>
                Loading…
              </Text>
            ) : items.length === 0 ? (
              <Text fontSize="sm" color="gray.500" p={4}>
                No mentions yet. When someone tags you with @, it shows up here.
              </Text>
            ) : (
              <VStack align="stretch" gap={0}>
                {items.map((item) => (
                  <button
                    key={item._id}
                    type="button"
                    onClick={() => openMention(item)}
                    style={{
                      textAlign: 'left',
                      padding: '12px',
                      borderBottom: '1px solid #f9fafb',
                      background: item.readAt ? 'white' : '#eff6ff',
                      border: 'none',
                      borderBottomWidth: 1,
                      borderBottomStyle: 'solid',
                      borderBottomColor: '#f9fafb',
                      width: '100%',
                      cursor: 'pointer',
                    }}
                  >
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800" lineClamp={1}>
                      {item.fromUser?.name || 'Someone'} mentioned you
                    </Text>
                    <Text fontSize="xs" color="gray.600" lineClamp={1} mt={0.5}>
                      {item.taskTitle} · {item.projectName}
                    </Text>
                    {item.preview && (
                      <Text fontSize="xs" color="gray.500" lineClamp={2} mt={1}>
                        {item.preview}
                      </Text>
                    )}
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      {formatRelative(item.createdAt)}
                    </Text>
                  </button>
                ))}
              </VStack>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MentionsBell;
